import React, { FC, useState } from 'react';
import { HiOutlineSave, HiOutlineX, HiOutlineCalendar } from 'react-icons/hi';
import { Button } from '../../../../../ui/Button';
import { BaseInput, SelectInput, DateInput } from '../../../../../commons';
import AccommodationManager from './AccommodationManager';

export interface Schedule {
  id: string;
  startDate: string;
  endDate: string;
  startLocation: string;
  endLocation: string;
  totalSlots: number;
  bookingType: 'contact' | 'instant';
  accommodations: any[];
}

interface NewScheduleFormProps {
  onSave: (schedule: Schedule) => void;
  onCancel: () => void;
  initialData?: Partial<Schedule>;
}

const NewScheduleForm: FC<NewScheduleFormProps> = ({ onSave, onCancel, initialData }) => {
  const [formData, setFormData] = useState<Partial<Schedule>>({
    startDate: '',
    endDate: '',
    startLocation: '',
    endLocation: '',
    totalSlots: 10,
    bookingType: 'contact',
    accommodations: [],
    ...initialData
  });

  const isValid = formData.startDate && formData.endDate && formData.startLocation;

  return (
    <div className='bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm animate-in fade-in zoom-in-95 duration-200'>
      <div className='flex justify-between items-center mb-6 border-b border-gray-100 dark:border-gray-700 pb-4'>
        <div className='flex items-center gap-3'>
            <div className='w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center text-primary-600 dark:text-primary-400'>
                <HiOutlineCalendar className='w-5 h-5' />
            </div>
            <h3 className='text-lg font-bold text-gray-900 dark:text-white'>
                {initialData ? 'Edit Schedule Details' : 'Configure New Schedule'}
            </h3>
        </div>
        <button 
            type='button' 
            onClick={onCancel} 
            className='p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all'
        >
            <HiOutlineX className='w-5 h-5' />
        </button>
      </div>

      <div className='space-y-6'>
        {/* Dates */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
           <DateInput 
             label='Start Date' 
             name='startDate'
             value={formData.startDate} 
             onChange={(newValue: string) => setFormData({...formData, startDate: newValue})}
             placeholder='Select start date'
           />
           <DateInput 
             label='End Date' 
             name='endDate'
             value={formData.endDate} 
             onChange={(newValue: string) => setFormData({...formData, endDate: newValue})}
             placeholder='Select end date'
             minDate={formData.startDate ? new Date(formData.startDate) : undefined}
           />
        </div>

        {/* Locations */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
           <div className='flex flex-col gap-1.5'>
               <label htmlFor='startLocation' className='block text-sm font-medium text-gray-700 dark:text-gray-200'>
                   Start Location
               </label>
               <BaseInput 
                 type='text' 
                 placeholder='e.g. Nairobi, Kenya'
                 name='startLocation'
                 id='startLocation'
                 value={formData.startLocation} 
                 onChange={(e: any) => setFormData({...formData, startLocation: e.target.value})}
               />
           </div>
           <div className='flex flex-col gap-1.5'>
               <label htmlFor='endLocation' className='block text-sm font-medium text-gray-700 dark:text-gray-200'>
                   End Location
               </label>
               <BaseInput 
                 type='text' 
                 placeholder='e.g. Mombasa, Kenya'
                 name='endLocation'
                 id='endLocation'
                 value={formData.endLocation} 
                 onChange={(e: any) => setFormData({...formData, endLocation: e.target.value})}
               />
           </div>
        </div>

        {/* Slots & Booking Type */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
           <div className='flex flex-col gap-1.5'>
               <label htmlFor='totalSlots' className='block text-sm font-medium text-gray-700 dark:text-gray-200'>
                   Total Slots (Capacity)
               </label>
               <BaseInput 
                 type='number' 
                 name='totalSlots'
                 id='totalSlots'
                 value={formData.totalSlots} 
                 onChange={(e: any) => setFormData({...formData, totalSlots: Number(e.target.value)})}
               />
           </div>
           <SelectInput
             label='Booking Action'
             name='bookingType'
             value={formData.bookingType}
             onChange={(newValue: any) => setFormData({...formData, bookingType: newValue})}
             options={[
               { label: 'Contact Us', value: 'contact' },
               { label: 'Instant Booking', value: 'instant' }
             ]}
           />
        </div>

        <div className='border-t border-gray-100 dark:border-gray-700 pt-6'>
            <AccommodationManager 
                accommodations={formData.accommodations || []}
                onChange={(accs) => setFormData({...formData, accommodations: accs})}
            />
        </div>

        <div className='flex justify-end gap-3 pt-6 border-t border-gray-100 dark:border-gray-700'>
            <Button variant='ghost' onClick={onCancel} className='font-bold uppercase tracking-wider text-xs'>
                Discard
            </Button>
            <Button 
                variant='primary' 
                onClick={() => onSave(formData as Schedule)}
                disabled={!isValid}
                leftIcon={<HiOutlineSave className='w-4 h-4' />}
                className='!px-8 font-bold'
            >
                {initialData ? 'Update Schedule' : 'Confirm Schedule'}
            </Button>
        </div>
      </div>
    </div>
  );
};

export default NewScheduleForm;
